import { APP_ID, APP_INITIALIZER, ApplicationConfig, inject, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, Router } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';

import { routes } from './app.routes';
import { idempotencyInterceptor } from './core/interceptors/auth-idempotency.interceptor';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { AuthService } from '~/core/services/auth.service';

/**
 * APP_INITIALIZER factory: Handles backend-first authentication
 * Waits for AuthService to validate JWT + RBAC with backend before app loads
 * If validation fails, redirects to unauthorized page
 */
function authInitializerFactory(): () => Promise<void> {
  return async () => {
    const authService = inject(AuthService);
    const router = inject(Router);
    
    try {
      console.info('[APP] Starting authentication initialization...');
      await authService.initialize();
      console.info('[APP] Authentication initialization successful');
    } catch (error) {
      console.error('[APP] Authentication initialization failed:', error);
      // Redirect to unauthorized page without throwing
      // This prevents the app from loading but allows navigation to work
      await router.navigate(['/unauthorized'], { replaceUrl: true });
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptors([authInterceptor, idempotencyInterceptor])),
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: authInitializerFactory,
    },
    // Security: Configure custom APP_ID to prevent DOM Clobbering via predictable ng-state element ID
    // Changes hydration state element ID from 'ng-state' to 'coo-cooass-hydration-state'
    { provide: APP_ID, useValue: 'coo-cooass-hydration' },
  ],
};
